import {
  comparePivotRowsByDimension,
  getPivotDimensionGroupKey,
  getPivotMetricNumeric,
  ordersRemainingFromRow,
  type LogisticPivotDimension,
  type LogisticPivotMetricColumnId,
  type LogisticPivotRow,
  type LogisticPivotRowMetrics,
} from '../models/LogisticPivot';
import type { PivotSortKey } from './sortPivotRows';
import { sortPivotRows } from './sortPivotRows';

/** Ordenación de la tabla aplicada también al desglose jerárquico (hermanos en cada nivel). */
export interface BuildPivotTreeSortOptions {
  sortKey: PivotSortKey;
  sortDir: 'asc' | 'desc';
}

export type PivotTreeNode =
  | {
      kind: 'branch';
      path: string;
      /** Índice de la última dimensión representada en este nodo (0-based). */
      depthIndex: number;
      /** Primera fila del grupo; sirve para pintar las columnas de dimensión hasta `depthIndex`. */
      sampleRow: LogisticPivotRow;
      metrics: LogisticPivotRowMetrics;
      children: PivotTreeNode[];
    }
  | {
      kind: 'leaf';
      path: string;
      depthIndex: number;
      row: LogisticPivotRow;
    };

function aggregateMetrics(rows: LogisticPivotRow[]): LogisticPivotRowMetrics {
  const orders_sent = rows.reduce((s, r) => s + (r.orders_sent ?? 0), 0);
  const orders_delivered = rows.reduce((s, r) => s + (r.orders_delivered ?? 0), 0);
  const orders_returned = rows.reduce((s, r) => s + (r.orders_returned ?? 0), 0);
  const orders_remaining = ordersRemainingFromRow({
    orders_sent,
    orders_delivered,
    orders_returned,
  });
  return { orders_sent, orders_delivered, orders_returned, orders_remaining };
}

function rowKeyForDim(row: LogisticPivotRow, dim: LogisticPivotDimension): string {
  return getPivotDimensionGroupKey(row, dim);
}

function compareDimKeys(
  dim: LogisticPivotDimension,
  ka: string,
  kb: string
): number {
  if (ka === '__null__' && kb === '__null__') return 0;
  if (ka === '__null__') return 1;
  if (kb === '__null__') return -1;
  if (dim === 'year' || dim === 'month') {
    const na = Number(ka);
    const nb = Number(kb);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  }
  return ka.localeCompare(kb, undefined, { numeric: true, sensitivity: 'base' });
}

function sortKeysForLevel(
  dim: LogisticPivotDimension,
  keys: string[],
  groups: Map<string, LogisticPivotRow[]>,
  sort: BuildPivotTreeSortOptions | undefined
): string[] {
  const baseAsc = (a: string, b: string) => compareDimKeys(dim, a, b);
  if (!sort) {
    return [...keys].sort(baseAsc);
  }
  const mult = sort.sortDir === 'desc' ? -1 : 1;
  const { sortKey } = sort;

  if (sortKey.startsWith('metric:')) {
    const id = sortKey.slice(7) as LogisticPivotMetricColumnId;
    return [...keys].sort((a, b) => {
      const ma = aggregateMetrics(groups.get(a)!);
      const mb = aggregateMetrics(groups.get(b)!);
      const cmp = (getPivotMetricNumeric(ma, id) - getPivotMetricNumeric(mb, id)) * mult;
      if (cmp !== 0) return cmp;
      return baseAsc(a, b);
    });
  }

  if (sortKey.startsWith('dim:')) {
    const sortDim = sortKey.slice(4) as LogisticPivotDimension;
    if (sortDim === dim) {
      return [...keys].sort((a, b) => baseAsc(a, b) * mult);
    }
    return [...keys].sort((a, b) => {
      const ra = groups.get(a)![0]!;
      const rb = groups.get(b)![0]!;
      const cmp = comparePivotRowsByDimension(ra, rb, sortDim) * mult;
      if (cmp !== 0) return cmp;
      return baseAsc(a, b);
    });
  }

  return [...keys].sort(baseAsc);
}

function buildLevel(
  rows: LogisticPivotRow[],
  dimensions: LogisticPivotDimension[],
  dimIndex: number,
  parentPath: string,
  sort: BuildPivotTreeSortOptions | undefined
): PivotTreeNode[] {
  const dim = dimensions[dimIndex];
  if (!dim) return [];

  const groups = new Map<string, LogisticPivotRow[]>();
  for (const r of rows) {
    const key = rowKeyForDim(r, dim);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  const sortedKeys = sortKeysForLevel(dim, [...groups.keys()], groups, sort);

  const isLast = dimIndex === dimensions.length - 1;

  const nodes: PivotTreeNode[] = [];

  for (const key of sortedKeys) {
    const groupRows = groups.get(key)!;
    const path = parentPath ? `${parentPath}›${dim}:${key}` : `${dim}:${key}`;

    if (isLast) {
      const leafRows =
        sort && groupRows.length > 1
          ? sortPivotRows(groupRows, sort.sortKey, sort.sortDir)
          : groupRows;
      for (let i = 0; i < leafRows.length; i++) {
        const row = leafRows[i]!;
        const leafPath = leafRows.length > 1 ? `${path}#${i}` : path;
        nodes.push({
          kind: 'leaf',
          path: leafPath,
          depthIndex: dimIndex,
          row,
        });
      }
    } else {
      const children = buildLevel(groupRows, dimensions, dimIndex + 1, path, sort);
      if (children.length === 0) continue;
      const sampleRow = groupRows[0]!;
      nodes.push({
        kind: 'branch',
        path,
        depthIndex: dimIndex,
        sampleRow,
        metrics: aggregateMetrics(groupRows),
        children,
      });
    }
  }

  return nodes;
}

/**
 * Construye un árbol de desglose: cada nivel sigue el orden de `dimensions`.
 * Con 0–1 ejes no hay ramas; el caller puede renderizar plano.
 */
export function buildPivotTree(
  rows: LogisticPivotRow[],
  dimensions: LogisticPivotDimension[],
  sort?: BuildPivotTreeSortOptions
): PivotTreeNode[] {
  if (rows.length === 0) return [];
  if (dimensions.length <= 1) {
    const ordered =
      sort && rows.length > 1
        ? sortPivotRows(rows, sort.sortKey, sort.sortDir)
        : rows;
    return ordered.map((row, i) => ({
      kind: 'leaf' as const,
      path: `leaf:${i}`,
      depthIndex: dimensions.length === 0 ? -1 : 0,
      row,
    }));
  }
  return buildLevel(rows, dimensions, 0, '', sort);
}

export interface VisiblePivotRow {
  node: PivotTreeNode;
  /** Profundidad en el árbol (0 = raíz) para sangría. */
  treeDepth: number;
}

/** Recorre el árbol en profundidad y devuelve nodos visibles según `expanded`. */
export function flattenVisibleTree(
  nodes: PivotTreeNode[],
  expanded: ReadonlySet<string>
): VisiblePivotRow[] {
  const out: VisiblePivotRow[] = [];

  const walk = (list: PivotTreeNode[], treeDepth: number) => {
    for (const n of list) {
      out.push({ node: n, treeDepth });
      if (n.kind === 'branch' && expanded.has(n.path)) {
        walk(n.children, treeDepth + 1);
      }
    }
  };

  walk(nodes, 0);
  return out;
}

/** Paths de todos los nodos rama (para expandir todo por defecto). */
export function collectBranchPaths(nodes: PivotTreeNode[]): string[] {
  const paths: string[] = [];
  const walk = (list: PivotTreeNode[]) => {
    for (const n of list) {
      if (n.kind === 'branch') {
        paths.push(n.path);
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return paths;
}
